import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, ChevronRight } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAlertBanner
 * ─────────────────────────────────────────────────────────────────────
 * Persistent banner that surfaces operational issues to admin/owner
 * users. Powered by `/api/me/alerts`. Today's primary signal is the
 * Shopify webhook health monitor (Feb-6-2026 incident: webhooks
 * failed silently for 17h after a credential rotation, customers
 * stopped flowing into journeys, and nobody noticed until a manual
 * UI inspection — this banner is how we prevent that going forward).
 *
 * Design choices:
 *   • Polls every 5 minutes — slow enough to be free, fast enough
 *     that the next admin login sees the alert.
 *   • Non-admins get an empty array from the API (handled server-side)
 *     so this component renders nothing for sales/support roles. Zero
 *     visual noise for users who can't act on the alert.
 *   • Dismiss = soft-snooze. The monitor job re-raises within 6h if
 *     the underlying issue is still active, so a problem can't be
 *     hidden forever by clicking X.
 *   • Critical alerts: red. Warning: amber. Action button routes to
 *     the page where the user can actually fix the problem.
 */
export default function AdminAlertBanner() {
  const [alerts, setAlerts] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const r = await axios.get(`${API_BASE_URL}/me/alerts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAlerts(r.data?.alerts || []);
    } catch {
      // Silent — banner failing to load must NEVER break the page.
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const dismiss = async (alertId) => {
    setAlerts((prev) => prev.filter((a) => a.id !== alertId));
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/me/alerts/${alertId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // If dismiss fails the user just refreshes — no toast spam.
    }
  };

  if (!alerts.length) return null;

  return (
    <div className="space-y-2 mb-4" data-testid="admin-alert-banner">
      {alerts.map((a) => {
        const isCritical = a.severity === 'critical';
        return (
          <div
            key={a.id}
            className={`rounded-lg border px-4 py-3 flex items-start gap-3 ${
              isCritical
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-100'
                : 'border-amber-500/40 bg-amber-500/10 text-amber-100'
            }`}
            data-testid={`admin-alert-${a.alert_key}`}
          >
            <AlertTriangle
              className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                isCritical ? 'text-rose-300' : 'text-amber-300'
              }`}
            />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{a.title}</div>
              <div className="text-xs mt-1 opacity-90">{a.message}</div>
            </div>
            {a.action_url && (
              <button
                onClick={() => navigate(a.action_url)}
                className={`text-xs px-3 py-1 rounded-md font-medium flex items-center gap-1 ${
                  isCritical
                    ? 'bg-rose-500/30 hover:bg-rose-500/50 text-white'
                    : 'bg-amber-500/30 hover:bg-amber-500/50 text-white'
                }`}
                data-testid={`admin-alert-action-${a.alert_key}`}
              >
                Fix it
                <ChevronRight className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => dismiss(a.id)}
              className="text-current opacity-60 hover:opacity-100"
              title="Dismiss (re-appears in 6h if still active)"
              data-testid={`admin-alert-dismiss-${a.alert_key}`}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
